import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from "axios";
import type { LoginResponse, User } from "../types/auth";

// API Base URL configuration
// Empty string = same-origin requests, proxied by webpack dev server
// The proxy configuration in craco.config.js forwards /api/* to the backend
const API_BASE_URL = "";

// Axios instance with httpOnly cookie support
const authClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

authClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const url = config.url ?? "";
    const method = (config.method ?? "get").toLowerCase();
    const isPlanningGet =
      method === "get" && url.includes("/planning/") && !url.includes("/alerts");
    const isAuthPath = url.includes("/auth/");
    if (isAuthPath || !isPlanningGet) {
      config.headers["Cache-Control"] = "no-cache, no-store";
      config.headers["Pragma"] = "no-cache";
    } else {
      const existing = String(config.headers["Cache-Control"] ?? "").toLowerCase();
      if (!existing.includes("no-store") && !existing.includes("no-cache")) {
        delete config.headers["Cache-Control"];
        delete config.headers["Pragma"];
      }
    }
    if (process.env.NODE_ENV === "development") {
      const w = window as unknown as {
        __RQ_TIMINGS?: Array<{ type: string; url: string; method: string; ts: number }>;
      };
      if (w.__RQ_TIMINGS === undefined) {
        w.__RQ_TIMINGS = [];
      }
      w.__RQ_TIMINGS.push({ type: "req", url, method, ts: Date.now() });
    }
    return config;
  },
  (error: AxiosError): Promise<never> => Promise.reject(error)
);

// Add response interceptor for automatic token refresh on 401
authClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    // Handle 403 errors with toast notification
    // 403 = "I know who you are, but you can't do this" → Show toast, don't retry
    if (error.response?.status === 403) {
      // Import toast emitter dynamically to avoid circular dependencies
      const { emit403Toast } = await import("../utils/toastEvents");

      // Extract error message from API response
      const apiMessage =
        (error.response.data as { detail?: string })?.detail ??
        (error.response.data as { message?: string })?.message;

      emit403Toast(apiMessage);
    }

    // Handle 401 errors with token refresh retry
    // NOTE: Only retry on 401 (Unauthorized), NOT 403 (Forbidden)
    // 401 = "Who are you?" → Token refresh may help
    if (
      originalRequest !== null &&
      originalRequest !== undefined &&
      error.response !== null &&
      error.response !== undefined &&
      error.response.status === 401 &&
      originalRequest._retry !== true
    ) {
      // Mark request as retried to prevent infinite loops
      originalRequest._retry = true;

      try {
        // Import refresh utilities (shared with api.ts)
        const { refreshAccessToken } = await import("../utils/tokenRefresh");

        // Attempt to refresh the token
        const newAccessToken = await refreshAccessToken();

        // Update Authorization header with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        // Retry the original request with new token
        return await authClient(originalRequest);
      } catch (refreshError) {
        // Token refresh failed - logout user and redirect to login
        const { logoutUser } = await import("../utils/tokenRefresh");
        logoutUser();

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// AUTH API METHODS
// ============================================

// Promise cache for getCurrentUser — prevents duplicate in-flight requests
// (same pattern as tokenRefresh.ts:refreshTokenPromise)
let currentUserPromise: Promise<User> | null = null;

export const authApi = {
  /** Login - tokens stored in httpOnly cookies by backend */
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await axios.post<LoginResponse>(
      "/api/auth/login",
      { email, password },
      { withCredentials: true }
    );
    return response.data;
  },

  /** Logout - backend reads refresh token from cookie and clears cookies */
  logout: async (): Promise<void> => {
    await axios.post("/api/auth/logout", {}, { withCredentials: true });
  },

  /** Refresh - backend reads refresh token from cookie, sets new cookies */
  refresh: async (): Promise<LoginResponse> => {
    const response = await axios.post<LoginResponse>(
      "/api/auth/refresh",
      {},
      { withCredentials: true }
    );
    return response.data;
  },

  /**
   * Get current user info.
   * Uses promise caching to deduplicate concurrent requests (e.g., StrictMode double-mount).
   */
  getCurrentUser: async (): Promise<User> => {
    if (currentUserPromise !== null) return currentUserPromise;
    currentUserPromise = authClient
      .get<{ data: User; success: boolean }>("/auth/me")
      .then((r) => r.data.data)
      .finally(() => {
        currentUserPromise = null;
      });
    return currentUserPromise;
  },

  /**
   * Change password (authenticated)
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await authClient.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  /**
   * Update current user's profile (self-service, no admin required)
   * Only allows updating full_name and timezone
   */
  updateMyProfile: async (data: {
    full_name?: string;
    timezone?: string;
    asana_pat?: string; // Non-empty sets the Asana PAT; "" clears it. Never echoed back.
  }): Promise<User> => {
    const response = await authClient.put<{ data: User; success: boolean; message: string }>(
      "/users/me",
      data
    );
    return response.data.data;
  },
};

// Export configured axios instance for other API calls
export { authClient };
